/**
 * Covering attorneys, and who has covered which court.
 *
 * Backend is `routes/CourtCoverage/CourtCoverageRouter.ts`. Note what is deliberately absent: there
 * is no update or delete for a coverage row. A row records that an attorney stood up at a court on
 * a day, and that history is the whole asset — the firm never recorded it before, which is why the
 * table exists. A mistake is corrected by deactivating the attorney, not by erasing where they went.
 */
export interface CoveringAttorneyDto {
  CoveringAttorney_guid: string;
  CoveringAttorney_Name: string;
  CoveringAttorney_Email: string | null;
  CoveringAttorney_Phone: string | null;
  CoveringAttorney_BarNumber: string | null;
  /** How far they will travel, where they have said. A fact about the person, not about the map. */
  CoveringAttorney_WillTravelMiles: number | null;
  CoveringAttorney_Notes: string | null;
  CoveringAttorney_Active: boolean;
}

export interface CourtCoverageDto {
  CourtCoverage_guid: string;
  CourtCoverage_CoveredOn: string;
  /** `staff` when a person entered it, `mailbox` when recovered from court correspondence. */
  CourtCoverage_Source: string;
  CourtCoverage_Notes: string | null;
  courtName: string | null;
  courtGuid: string | null;
  attorneyName: string | null;
  attorneyGuid: string | null;
  /** The setting it was for. Null when the attorney took a whole docket. */
  courtEventGuid: string | null;
}

export interface CoverageSuggestionDto {
  attorneyGuid: string;
  attorneyName: string;
  appearances: number;
  basis: 'same-court' | 'same-county' | 'same-locality' | 'unrelated';
  viaCourtName: string;
  /** One line a person can read and disagree with. Always shown. */
  reason: string;
}

declare const process: { env: { API_HOST?: string } };

export default class CoverageApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  private static async json<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await fetch(`${CoverageApi.host()}${path}`, {
      credentials: 'include',
      ...init,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || 'Request failed');
    }
    return response.json();
  }

  static async listAttorneys(includeInactive = false): Promise<CoveringAttorneyDto[]> {
    const query = includeInactive ? '?includeInactive=true' : '';
    const body = await CoverageApi.json<{ attorneys: CoveringAttorneyDto[] }>(
      `/api/v1/manager/covering-attorneys${query}`,
    );
    return body.attorneys;
  }

  static async createAttorney(input: {
    CoveringAttorney_Name: string;
    CoveringAttorney_Email?: string | null;
    CoveringAttorney_Phone?: string | null;
    CoveringAttorney_BarNumber?: string | null;
    CoveringAttorney_WillTravelMiles?: number | null;
  }): Promise<CoveringAttorneyDto> {
    return CoverageApi.json<CoveringAttorneyDto>('/api/v1/manager/covering-attorneys', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  /**
   * Contact details and status. Never their appearances — the backend has no route for that, on
   * purpose.
   */
  static async updateAttorney(
    guid: string,
    patch: Partial<Omit<CoveringAttorneyDto, 'CoveringAttorney_guid'>>,
  ): Promise<CoveringAttorneyDto> {
    return CoverageApi.json<CoveringAttorneyDto>(
      `/api/v1/manager/covering-attorneys/${guid}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      },
    );
  }

  static async setAttorneyActive(
    guid: string,
    active: boolean,
  ): Promise<CoveringAttorneyDto> {
    return CoverageApi.updateAttorney(guid, { CoveringAttorney_Active: active });
  }

  static async listCoverage(params: {
    courtGuid?: string;
    attorneyGuid?: string;
    ticketGuid?: string;
  } = {}): Promise<CourtCoverageDto[]> {
    const search = new URLSearchParams();
    if (params.courtGuid) search.set('courtGuid', params.courtGuid);
    if (params.attorneyGuid) search.set('attorneyGuid', params.attorneyGuid);
    if (params.ticketGuid) search.set('ticketGuid', params.ticketGuid);
    const query = search.toString() ? `?${search.toString()}` : '';
    const body = await CoverageApi.json<{ coverage: CourtCoverageDto[] }>(
      `/api/v1/manager/court-coverage${query}`,
    );
    return body.coverage;
  }

  /**
   * Record an appearance: either against a named setting, or against a court and a day.
   *
   * With `CourtEvent_guid` the backend reads the court and the day off the setting itself, and
   * ignores anything else passed alongside. Use the court and date form only for a docket
   * appearance, where there is no single setting to point at.
   */
  static async recordCoverage(input: {
    CoveringAttorney_guid: string;
    CourtEvent_guid?: string;
    Court_guid?: string;
    CourtCoverage_CoveredOn?: string;
    CourtCoverage_Notes?: string | null;
  }): Promise<{ CourtCoverage_guid: string }> {
    return CoverageApi.json<{ CourtCoverage_guid: string }>(
      '/api/v1/manager/court-coverage',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      },
    );
  }

  static async suggestionsFor(courtGuid: string): Promise<CoverageSuggestionDto[]> {
    const body = await CoverageApi.json<{ suggestions: CoverageSuggestionDto[] }>(
      `/api/v1/manager/court/${courtGuid}/coverage-suggestions`,
    );
    return body.suggestions;
  }
}
